import { useSignals } from "@preact/signals-react/runtime";
import { signal } from "@preact/signals-react";
import { InfiniteScroll } from "@/components/blocks/infinite-scroll";
import { Button } from "@/components/ui/button";
import { MoveLeft, Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChatMessage } from "@/pages/subs/chats/chat-message";
import { useSocket } from "@/providers/socket-provider";
import { useChats, onNewMessage } from "@/providers/chat-provider";
import { useTranslation } from "@/providers/language-provider";
import { useAuth } from "@/providers/auth-provider";
import { MessageInput } from "@/components/blocks/message-input";
import { sharedData } from "@/features/shared-data-manager";
import { VideoPlayer, VideoInfo } from "@/components/blocks/video-player";

const chatData = signal<{
  messages: any[];
  hasMore: boolean;
}>({
  messages: [],
  hasMore: true,
});

export const ChatView = ({
  chatSettings,
}: {
  chatSettings: (chat: any) => void;
}) => {
  useSignals();
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const { isConnected } = useSocket();
  const { currentChat, selectChat, sendChatMessage, fetchMessages } =
    useChats();
  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState("");
  const isLoadComplete = useRef<boolean>(false);
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initChat();
    console.log("[TEST] current chat: ", currentChat);
  }, [currentChat]);

  const initChat = () => {
    console.log("ChatView init.");
    setIsLoading(false);
    setText("");
    setVideoInfo(null);
    chatData.value = {
      messages: [],
      hasMore: true,
    };
    onNewMessage.value = currentChat ? handleNewMessage : null;
    isLoadComplete.current = false;
  };

  const onLoadComeplete = () => {
    handleSharedData();
  };

  const handleSharedData = () => {
    const data = sharedData.value;
    if (currentChat && data) {
      // const message = `${data.title}\n${data.text}\n${data.url}`;
      const message = `${data.title}\n${data.url}`;
      console.log("Shared Data:", data, ", message: ", message);
      sendMessageToServer(message);
      sharedData.value = null;
    }
  };

  const handleNewMessage = async (newMessage: any) => {
    console.log("handleNewMessage:", newMessage);
    let isUpdated = false;
    const updatedMessages = chatData.value.messages.map((msg) => {
      if (msg._id === newMessage.tempId) {
        const { tempId, ...messageWithoutTempId } = newMessage;
        isUpdated = true;
        return messageWithoutTempId;
      }
      return msg;
    });

    chatData.value = {
      ...chatData.value,
      messages: isUpdated ? updatedMessages : [newMessage, ...updatedMessages],
    };
  };

  const loadMessages = useCallback(async () => {
    if (!currentChat) return;
    if (isLoading) return;
    if (!chatData.value.hasMore) return;

    // if (!isConnected) {
    //   setIsLoading(true);
    //   await new Promise((resolve) => setTimeout(resolve, 2000));
    //   setIsLoading(false);
    //   return;
    // }

    try {
      setIsLoading(true);
      const loadFromStart = chatData.value.messages.length === 0;
      const newData = await fetchMessages({
        chatId: currentChat._id,
        loadFromStart,
      });
      console.log("ChatView loadMessages", newData);
      chatData.value = newData;
      // first load.
      if (!isLoadComplete.current) {
        onLoadComeplete();
        isLoadComplete.current = true;
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentChat, isLoading, isConnected, onLoadComeplete]);

  const onClickSend = () => {
    sendMessageToServer(text.trim());
    setText("");
  };

  const sendMessageToServer = (message: string) => {
    if (currentChat && message) {
      const tempMessage = createTempMessage(message);
      // 즉시 화면에 임시 메시지 추가
      chatData.value = {
        ...chatData.value,
        messages: [tempMessage, ...chatData.value.messages],
      };

      sendChatMessage(message, tempMessage._id);
      console.log("sendMessageToServer", tempMessage);
    }
  };

  const createTempMessage = (content: string) => {
    const tempId = `temp-${Date.now()}`;
    return {
      _id: tempId,
      content,
      sender: { _id: user._id }, // user 정보는 AuthProvider에서 가져와야 함
      chat: { _id: currentChat._id },
      isMe: true,
      isPending: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  };

  const handleSettings = () => {
    chatSettings(currentChat);
  };

  const handlePlayVideo = (info: VideoInfo) => {
    setVideoInfo(info);
  };

  // 가상 키보드로 인한 높이 변화 대응(flex-col-reverse로 인해 스크롤 위치가 변경되는 문제 해결)
  useEffect(() => {
    // Android 환경 체크
    const isAndroid = /android/i.test(navigator.userAgent);

    if (!isAndroid) return;

    // visualViewport 이벤트 리스너 등록
    const updateHeight = () => {
      if (containerRef.current && window.visualViewport) {
        // visualViewport의 높이를 가져와서 설정
        const height = window.visualViewport.height;
        containerRef.current.style.height = `${height}px`;
        containerRef.current.style.maxHeight = `${height}px`;
      }
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateHeight);
      window.visualViewport.addEventListener("scroll", updateHeight);
      updateHeight(); // 초기 설정
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateHeight);
        window.visualViewport.removeEventListener("scroll", updateHeight);
      }
    };
  }, [currentChat?._id]);

  if (!currentChat) {
    return (
      <div className="flex-1 flex items-center justify-center">
        {t("SelectChatToStartMessaging")}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col w-full h-full overflow-hidden"
    >
      {videoInfo && (
        <VideoPlayer info={videoInfo} onClose={() => setVideoInfo(null)} />
      )}
      {/* 헤더 */}
      <div className="flex-shrink-0 border-b py-1 px-3">
        <div className="grid grid-cols-3 items-center">
          <Button
            variant="ghost"
            size="icon"
            className="justify-self-start"
            onClick={() => selectChat(null)}
          >
            <MoveLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-md justify-self-center">
            {currentChat.friend
              ? currentChat.friend.name
              : currentChat.chatName}
          </h3>
          <div className="justify-self-end">
            <Button
              variant="ghost"
              size="icon"
              className="justify-self-start"
              onClick={handleSettings}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* 메시지 영역 */}
      <div className="flex-1 overflow-hidden">
        <InfiniteScroll
          items={chatData.value.messages}
          itemRender={(index, message) => {
            return (
              <ChatMessage
                key={message._id || index}
                chat={currentChat}
                index={index}
                allMessages={chatData.value.messages}
                message={message}
                locale={language}
                onPlayVideo={handlePlayVideo}
              />
            );
          }}
          loadMore={loadMessages}
          hasMore={chatData.value.hasMore}
          isLoading={isLoading}
          reverseOrder={true}
          className="p-1"
        />
      </div>

      {/* 입력창 */}
      <div className="flex-shrink-0 p-1 border-t">
        <div className="flex w-full bg-secondary rounded-md items-center px-1 ">
          <MessageInput
            message={text}
            setMessage={setText}
            onSend={onClickSend}
            onSendDirect={sendMessageToServer}
            locale={language}
          />
        </div>
      </div>
    </div>
  );
};
